import React, { useEffect, useState } from 'react';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import axios from 'axios';
import { EmptyState, StatCard, type DataTableColumn } from '@routes-plan/ui';
import { formatCurrency } from '../../../utils/format/currency';
import { useT } from '../../../i18n';
import { ModalTable, ReportStats } from '../components/ReportLayout';

interface Props {
  open: boolean;
  onClose: () => void;
  officeId: number;
  officeName: string;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}


const OfficeCashBoxesModal: React.FC<Props> = ({
  open,
  onClose,
  officeId,
  officeName,
  showAlert,
}) => {
  const t = useT();
  const [data, setData] = useState<any[]>([]);
  // Cargando propio del modal (el general queda detrás).
  const { loading, showLoading, hideLoading } = useModalLoading();

  useEffect(() => {
    if (!open || !officeId) {
      setData([]);
      return;
    }

    const fetchBoxDetails = async () => {
      setData([]);
      showLoading();
      try {
        const response = await axios.get(`/reports/offices/${officeId}/route-cash-boxes`);
        const list = response.data?.data;
        setData(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error al cargar detalles de cajas:', error);
        showAlert(t('cashReports.officeBoxes.loadError'), 'error');
        setData([]);
      } finally {
        hideLoading();
      }
    };

    void fetchBoxDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, officeId]);

  const rows = data.map((box) => ({
    id: box.id,
    type: box.type?.name || t('cashReports.officeBoxes.noType'),
    balance: box.balance,
    date: box.date,
    routeName: box.route?.name,
  }));

  type BoxRow = (typeof rows)[number];
  const columns: DataTableColumn<BoxRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.routeName || '-', sortable: true },
    { id: 'balance', header: t('cashReports.officeBoxes.balance'), value: (r) => Number(r.balance ?? 0), render: (r) => formatCurrency(Number(r.balance ?? 0)), align: 'right', sortable: true },
  ];
  const total = rows.reduce((sum, r) => sum + Number(r.balance ?? 0), 0);

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t('cashReports.officeBoxes.title')}
      subtitle={t('cashReports.officeBoxes.officeLabel', { name: officeName })}
      loading={loading}
      maxWidth={760}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <ReportStats>
            <StatCard label={t('cashReports.officeBoxes.routeCount')} value={rows.length} />
            <StatCard label={t('cashReports.officeBoxes.total')} value={formatCurrency(total)} />
          </ReportStats>
          <ModalTable
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            exportFileName={t('cashReports.officeBoxes.exportFileName', { id: officeId })}
            ariaLabel={t('cashReports.officeBoxes.title')}
          />
        </Box>
      ) : loading ? null : (
        <EmptyState dense title={t('cashReports.officeBoxes.empty')} />
      )}
    </StandardModal>
  );
};

export default OfficeCashBoxesModal;
